import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, ImagePlus, Loader2, Check, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useT } from '../context/LanguageContext';
import { inputCls } from './admin/EditorShell';

export const SubmitPhotoButton = ({ className = '' }) => {
  const { user } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" data-testid="submit-photo-button" onClick={() => (user ? setOpen(true) : navigate('/login'))} className={className}>
        <Camera size={18} className="inline -mt-0.5 mr-2" />{t('Send inn bilete')}
      </button>
      {open && <SubmitPhotoDialog onClose={() => setOpen(false)} />}
    </>
  );
};

export const SubmitPhotoDialog = ({ onClose, onDone }) => {
  const { user } = useAuth();
  const { t } = useT();
  const [file, setFile] = useState(null);
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const fileRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Vel eit bilete først.');
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('caption', caption);
      const { data } = await api.post('/gallery/submit', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDone(data); onDone?.(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[95] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="submit-photo-dialog">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-md rounded-3xl bg-[#0f0a17] border border-white/10 p-7 shadow-2xl animate-[fadeUp_0.25s_ease-out]">
        <button type="button" onClick={onClose} aria-label="Lukk" data-testid="submit-photo-close" className="absolute top-4 right-4 w-10 h-10 rounded-full border border-white/15 text-white flex items-center justify-center hover:border-[#ff2e2e] hover:text-[#ff2e2e]"><X size={18} /></button>
        <h3 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{t('Send inn bilete')}</h3>
        {done ? (
          <div className="text-center py-6" data-testid="submit-photo-success">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/15 text-emerald-300 flex items-center justify-center mb-3"><Check size={26} /></div>
            <p className="text-white font-bold">{done.status === 'approved' ? t('Biletet er publisert i galleriet!') : t('Takk! Biletet blir vist når ein administrator har godkjent det.')}</p>
            <button type="button" onClick={onClose} className="mt-5 px-6 py-3 rounded-full bg-[#ff2e2e] text-black font-bold">OK</button>
          </div>
        ) : (
          <>
            <p className="text-white/55 text-sm mb-5">{user?.is_admin ? t('Som administrator blir biletet publisert med ein gong.') : t('Bileta blir sjekka av ein administrator før dei kjem i galleriet.')}</p>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="submit-photo-input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="submit-photo-pick" className="w-full aspect-[3/2] rounded-2xl border border-dashed border-white/20 bg-[#1a1424] overflow-hidden relative flex items-center justify-center text-white/50 hover:border-[#ff2e2e] hover:text-[#ff2e2e]">
              {file ? <img src={URL.createObjectURL(file)} alt="" className="absolute inset-0 w-full h-full object-cover" /> : null}
              <span className="relative inline-flex items-center gap-2 bg-black/60 px-4 py-2 rounded-full text-sm font-semibold"><ImagePlus size={16} /> {file ? 'Byt bilete' : 'Vel bilete'}</span>
            </button>
            <input className={`${inputCls} mt-3`} placeholder={t('Kort bilettekst (valfritt)')} value={caption} onChange={(e) => setCaption(e.target.value)} maxLength={140} data-testid="submit-photo-caption" />
            <button type="submit" disabled={busy} data-testid="submit-photo-submit" className="mt-4 w-full py-3.5 rounded-full bg-[#ff2e2e] text-black font-bold uppercase tracking-wide hover:bg-[#ff4d4d] disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />} {t('Send inn')}
            </button>
          </>
        )}
      </form>
    </div>,
    document.body
  );
};
